import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Circle, Loader2, Rocket, ArrowRight, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const jumpTo = (testid) => {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") setTimeout(() => el.focus(), 600);
};

export default function GettingStarted({ authHeader, refreshKey = 0, reopenKey = 0, onStatus }) {
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const boxRef = useRef(null);

  const report = useCallback(
    (d) => {
      if (onStatus && d) {
        onStatus({ percent: d.percent, complete: d.complete, done: d.done_count, total: d.total });
      }
    },
    [onStatus],
  );

  const load = useCallback(() => {
    axios
      .get(`${API}/mlo/onboarding`, { headers: authHeader() })
      .then((r) => {
        setData(r.data);
        report(r.data);
      })
      .catch(() => setData(null));
  }, [authHeader, report]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Header "Getting started" link brings the card back after it's been dismissed.
  useEffect(() => {
    if (!reopenKey) return;
    setHidden(false);
    setExpanded(true);
    setData((d) => (d ? { ...d, dismissed: false } : d));
    axios
      .post(`${API}/mlo/onboarding/dismiss`, { dismissed: false }, { headers: authHeader() })
      .catch(() => {});
    const t = setTimeout(
      () => boxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
      120,
    );
    return () => clearTimeout(t);
  }, [reopenKey, authHeader]);

  const ack = async (key, done) => {
    setBusyKey(key);
    try {
      const { data: fresh } = await axios.post(
        `${API}/mlo/onboarding/ack`,
        { key, done },
        { headers: authHeader() },
      );
      setData(fresh);
      report(fresh);
    } catch {
      toast.error("Couldn't update that step.");
    } finally {
      setBusyKey("");
    }
  };

  if (!data || hidden) return null;

  const { steps, done_count: done, total, percent } = data;
  const nextStep = steps.find((s) => !s.done);

  // Once every step is ticked the card gets out of the way on its own — a single
  // line, and the parent moves it to the bottom of the page.
  if (data.complete && !expanded) {
    return (
      <div
        ref={boxRef}
        className="rounded-2xl border border-linen-border bg-white shadow-soft px-5 py-3 flex items-center justify-between gap-3"
        data-testid="mlo-getting-started-slim"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="shrink-0 w-6 h-6 rounded-full bg-teal text-white flex items-center justify-center">
            <Check size={13} strokeWidth={3} />
          </span>
          <span className="text-sm text-navy font-medium truncate">You're fully set up.</span>
          <span className="hidden sm:inline text-xs text-ink-500 truncate">
            All {total} setup steps done — from here it's just reps.
          </span>
        </div>
        <button
          onClick={() => setExpanded(true)}
          data-testid="mlo-getting-started-review"
          className="shrink-0 text-[11px] font-semibold text-teal hover:underline"
        >
          Review checklist
        </button>
      </div>
    );
  }

  return (
    <div
      ref={boxRef}
      className="rounded-3xl border border-linen-border shadow-soft overflow-hidden bg-white"
      data-testid="mlo-getting-started"
    >
      <div className="relative px-6 sm:px-8 pt-6 pb-5" style={{ background: "linear-gradient(120deg,#0F2A47 0%,#17385e 58%,#1F8F8B 140%)" }}>
        {data.complete && (
          <button
            onClick={() => setExpanded(false)}
            data-testid="mlo-getting-started-dismiss"
            className="absolute top-4 right-4 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Collapse setup checklist"
          >
            <X size={16} />
          </button>
        )}
        <div className="flex items-start gap-5">
          <div className="shrink-0 relative w-16 h-16">
            <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="3.5" />
              <circle
                cx="18" cy="18" r="15.5" fill="none" stroke="#A6D24A" strokeWidth="3.5" strokeLinecap="round"
                strokeDasharray={`${(percent / 100) * 97.4} 97.4`}
                style={{ transition: "stroke-dasharray .6s cubic-bezier(.22,1,.36,1)" }}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-white font-semibold text-sm tabular-nums">
              {percent}%
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#A6D24A]">
              <Rocket size={12} /> Getting started
            </div>
            <h2 className="mt-0.5 font-heading font-medium text-2xl text-white">
              {data.complete ? "You're fully set up." : `${done} of ${total} done`}
            </h2>
            <p className="mt-1 text-sm text-white/75 max-w-xl">
              {data.complete
                ? "Profile complete, routing live, links in hand. From here it's just reps — send your links and log the wins."
                : nextStep
                  ? `Next up: ${nextStep.label.toLowerCase()}. Takes a couple of minutes.`
                  : "A few short steps and your page is working for you."}
            </p>
          </div>
        </div>
      </div>

      <ul className="divide-y divide-linen-border" data-testid="mlo-getting-started-steps">
        {steps.map((s, i) => (
          <li
            key={s.key}
            className={`flex items-start gap-3.5 px-5 sm:px-7 py-4 transition-colors ${s.done ? "bg-linen/30" : "hover:bg-linen/40"}`}
            data-testid={`mlo-step-${s.key}`}
          >
            <button
              type="button"
              onClick={() => s.manual && ack(s.key, !s.done)}
              disabled={!s.manual || busyKey === s.key}
              data-testid={`mlo-step-toggle-${s.key}`}
              title={s.manual ? "Mark this step done" : "Checked off automatically once your profile has it"}
              className={`shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center border transition-all ${
                s.done
                  ? "bg-teal border-teal text-white"
                  : s.manual
                    ? "border-ink-300 text-ink-300 hover:border-teal hover:text-teal cursor-pointer"
                    : "border-linen-border text-ink-300 cursor-default"
              }`}
            >
              {busyKey === s.key ? <Loader2 size={12} className="animate-spin" /> : s.done ? <Check size={13} strokeWidth={3} /> : <Circle size={8} fill="currentColor" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold ${s.done ? "text-ink-400 line-through decoration-ink-300" : "text-navy"}`}>
                <span className="tabular-nums text-ink-400 font-normal mr-1.5">{i + 1}.</span>
                {s.label}
              </div>
              {!s.done && <p className="mt-1 text-[13px] text-ink-500 leading-relaxed">{s.why}</p>}
            </div>
            {!s.done && (
              <button
                onClick={() => jumpTo(s.target)}
                data-testid={`mlo-step-cta-${s.key}`}
                className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold bg-navy text-white px-3 py-1.5 rounded-full hover:bg-[#173a5f] transition-colors"
              >
                {s.cta} <ArrowRight size={11} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
